import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { useRecoilValue } from "recoil";
import { userState } from "@/lib/store";
import { saveSettings } from "@/controllers/auth";
import { AutoTourEntry, getTutorial, TutorialStepData } from "@/controllers/tutorials";
import { matchAutoTour } from "@/components/tour/matchAutoTour";
import { loadAutoTours } from "@/components/tour/usePageTour";
import TourGuide, { TOUR_START_EVENT, areToursDisabled, hasTourDismissal, isTourMounted } from "@/components/tour/TourGuide";
import { useIdleEffect } from "@/lib/defer";

/**
 * App-wide tour runner (Appcues replacement) — mounted once in _app.
 *
 * Two triggers:
 *   - a `?tour=<tour_id>` query param (Help-menu links, the post-signup
 *     `/markets-overview?tour=welcome` landing) — replaces the old
 *     `?appcue=<flow_id>` links;
 *   - a TOUR_START_EVENT dispatched via `startTour()` in TourGuide.tsx
 *     (the post-payment "subscription_welcome" message, Help entries
 *     with no page link) — replaces `window.Appcues.show(...)`.
 *
 * Pages that mount their own useTour instance (options analyze / flow)
 * keep owning their tour: a start request for a mounted tour id is
 * handled by that instance, so this runner only launches tours with no
 * local mount. Explicit requests deliberately ignore the per-user
 * dismissal record — dismissal only suppresses auto-starts.
 */

// A page mounting its own useTour registers during the same render
// pass, but data-dependent pages can mount their tour late — give them
// a beat before deciding who runs a ?tour= request.
const PAGE_MOUNT_GRACE_MS = 600;

const GlobalTourLauncher = () => {
    const router = useRouter();
    const user = useRecoilValue(userState);
    const [tourId, setTourId] = useState<string | null>(null);
    const [steps, setSteps] = useState<TutorialStepData[]>([]);
    const [open, setOpen] = useState(false);

    const launch = useCallback((id: string) => {
        getTutorial(id)
            .then((res) => {
                if (!res.steps?.length) return; // unknown tour / targeting failed closed
                setTourId(id);
                setSteps(res.steps);
                setOpen(true);
            })
            .catch(() => {
                /* content unavailable — no tour */
            });
    }, []);

    // ?tour= deep links. The ref stops the same param from re-launching
    // on unrelated route changes while it sits in the URL; it resets
    // when the param clears so a fresh link works again.
    const lastQueryTour = useRef<string | null>(null);
    useEffect(() => {
        const id = typeof router.query.tour === "string" ? router.query.tour : "";
        if (!id) {
            lastQueryTour.current = null;
            return;
        }
        if (!router.isReady || id === lastQueryTour.current) return;
        lastQueryTour.current = id;
        const t = setTimeout(() => {
            if (isTourMounted(id)) {
                window.dispatchEvent(new CustomEvent(TOUR_START_EVENT, { detail: { tourId: id } }));
            } else {
                launch(id);
            }
        }, PAGE_MOUNT_GRACE_MS);
        return () => clearTimeout(t);
    }, [router.isReady, router.query.tour, launch]);

    // ── Route auto-show ──
    // The server supplies {id, route} pairs (Tutorial.auto_route,
    // audience-gated). A route is one or more Next.js pathname patterns,
    // so "/s/[exchange]/[ticker]/short-interest" fires once per user
    // across every stock — "once per page TYPE". Auto-shows respect the
    // per-tour dismissal AND the global "turn off all guides" opt-out
    // (unlike explicit ?tour=/startTour requests, which bypass both).
    const [autoTours, setAutoTours] = useState<AutoTourEntry[]>([]);
    const autoShown = useRef<Set<string>>(new Set());
    // The only tutorials call on the page-load path, and deliberately the
    // cheap one: `getAutoTourRoutes` is the same answer for every visitor, so
    // the server serves it from cache and the browser keeps it. The Help-menu
    // listing is per-user and used only when the Help menu opens, which is now
    // the only thing that asks for it.
    //
    // Deferred to idle on top of that: an auto-shown tour already waits
    // PAGE_MOUNT_GRACE_MS after the route resolves before it can open, so the
    // map behind it has nothing to gain from racing the page's own data.
    useIdleEffect(() => {
        let cancelled = false;
        loadAutoTours().then((auto) => {
            if (!cancelled) setAutoTours(auto);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!router.isReady || open || !autoTours.length) return;
        const entry = matchAutoTour(autoTours, router.pathname, router.asPath);
        if (!entry || autoShown.current.has(entry.id)) return;
        // Same grace as ?tour= links: a page mounting its own useTour
        // (which auto-shows by itself) may register a beat later than
        // this effect fires.
        const t = setTimeout(() => {
            if (autoShown.current.has(entry.id)) return;
            if (isTourMounted(entry.id)) return; // the page's own useTour owns it
            // Signed-out visitors (most of them ad or search traffic landing on a
            // data page) are not ambushed by a tour: the locked-state panel offers
            // "Take the tour" instead (LockedValuePanel). Wait for the user to load
            // so a visitor is never shown a tour in the gap before we know.
            if (!user?.subscription_plan || user.subscription_plan === "ORTEX Visitor") return;
            if (areToursDisabled(user) || hasTourDismissal(entry.id, user)) return;
            // Once per mount even if the user skips without persisting — an
            // auto-show must never re-ambush on every route change.
            autoShown.current.add(entry.id);
            launch(entry.id);
        }, PAGE_MOUNT_GRACE_MS);
        return () => clearTimeout(t);
    }, [router.isReady, router.pathname, router.asPath, autoTours, open, user, launch]);

    // Programmatic starts (startTour) for tours with no page-mounted
    // instance — page instances handle their own ids via useTour.
    useEffect(() => {
        const onStart = (e: Event) => {
            const id = (e as CustomEvent).detail?.tourId;
            if (typeof id === "string" && id && !isTourMounted(id)) launch(id);
        };
        window.addEventListener(TOUR_START_EVENT, onStart);
        return () => window.removeEventListener(TOUR_START_EVENT, onStart);
    }, [launch]);

    // Same persistence contract as useTour's close: "Don't show again" /
    // Done record the opt-out so the tour won't auto-ambush later; Skip
    // hides it for this visit only.
    const close = (persist: boolean) => {
        setOpen(false);
        if (!persist || !tourId) return;
        try {
            window.localStorage.setItem(`ortex.tour.${tourId}.dismissed`, "1");
            window.sessionStorage.setItem(`ortex.tour.${tourId}.dismissed.session`, "1");
        } catch {
            /* private mode etc. */
        }
        if (user?.settings) {
            saveSettings(tourId, "tour", { dismissed: true, at: new Date().toISOString() }).catch(() => {
                /* non-fatal — the localStorage flag still holds this browser */
            });
        }
    };

    return <TourGuide steps={steps} open={open} onClose={close} tourId={tourId ?? undefined} />;
};

export default GlobalTourLauncher;
